import { getPublicPricing } from "@/lib/pricing-public";
import { MARKETING_SOLO_TRIAL_LABEL } from "@/lib/plan-features-public";

/** Cell value: true = yes, false = no/missing, string = specific text for that column */
export type CompareCell = boolean | string;

export type ComparePage = {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  competitorName: string;
  competitorHint?: string;
  rows: { feature: string; jorvio: CompareCell; them: CompareCell }[];
  whenThemFits?: string;
  whenJorvioFits: string;
  /** If false, omit from footer / sitemap. Page only included when we're competitive. */
  publish: boolean;
};

const starterPrice = getPublicPricing().starter;

/**
 * Honest comparisons where Jorvio is a strong fit for quote-first contractors.
 * Omitted: head-to-heads where the competitor clearly wins for our core ICP (e.g. enterprise dispatch suites).
 */
export const COMPARE_PAGES: ComparePage[] = [
  {
    slug: "jobber",
    publish: true,
    metaTitle: "Jorvio Quotes vs Jobber for contractors",
    metaDescription:
      "Compare Jorvio and Jobber on pricing, quotes, customer portal, and trial terms for solo and small trade shops.",
    competitorName: "Jobber",
    competitorHint: "jobber.com",
    rows: [
      { feature: "Starting price (1 user)", jorvio: `from $${starterPrice}/mo`, them: "$39/mo (Core)" },
      { feature: "Extra full-access user", jorvio: "$15/user/mo", them: "$29/user/mo" },
      { feature: "Crew calendar seats included (Pro)", jorvio: "Up to 10 view-only", them: "Paid users only" },
      { feature: "Customer portal (review, sign, pay)", jorvio: true, them: true },
      { feature: "Line-item proposals & templates", jorvio: true, them: true },
      { feature: "AI quote drafting from a job description", jorvio: true, them: false },
      { feature: "Live material price finder with buy links (Pro & Agency)", jorvio: true, them: false },
      { feature: "In-app customer messaging", jorvio: true, them: true },
      { feature: "GPS dispatch & route optimization", jorvio: false, them: true },
      { feature: "Free trial without card (any plan)", jorvio: MARKETING_SOLO_TRIAL_LABEL, them: "14-day trial" },
    ],
    whenThemFits:
      "Jobber makes sense when you run a larger crew, need GPS dispatch and route optimization, and want CRM-style client records at the center of the product.",
    whenJorvioFits:
      "Jorvio is a good fit when quotes and customer approval speed matter most. Starter is $19/mo and includes AI drafting with a daily limit. Higher tiers add unlimited AI, live material pricing, and Stripe payments. Extra seats are $15/user (Jobber charges $29). You get quoting, jobs, portal, and deposits without paying for a full dispatch suite you may not use.",
  },
  {
    slug: "housecall-pro",
    publish: true,
    metaTitle: "Jorvio Quotes vs Housecall Pro for contractors",
    metaDescription:
      "Compare Jorvio and Housecall Pro on transparent pricing, quotes, and customer payments for home-service contractors.",
    competitorName: "Housecall Pro",
    competitorHint: "housecallpro.com",
    rows: [
      { feature: "Starting price (solo shop)", jorvio: `from $${starterPrice}/mo`, them: "$79/mo ($59 billed annually)" },
      { feature: "No per-feature add-on paywalls", jorvio: true, them: "GPS, Sales Proposal & Price Book need higher tiers/add-ons" },
      { feature: "Typical real cost after add-ons", jorvio: "List price is what you pay", them: "Often $150–350+/mo with add-ons" },
      { feature: "Core proposal & price book without add-ons", jorvio: true, them: "Sales Proposal & Price Book are paid add-ons" },
      { feature: "Unlimited sent quotes on every plan", jorvio: true, them: true },
      { feature: "Customer portal (review, sign, pay)", jorvio: true, them: true },
      { feature: "In-app customer messaging", jorvio: true, them: true },
      { feature: "AI quote drafting + live material price finder", jorvio: true, them: false },
      { feature: "Built-in marketing automation", jorvio: false, them: "Strong on higher tiers" },
      { feature: "Free trial without card (any plan)", jorvio: MARKETING_SOLO_TRIAL_LABEL, them: "14-day trial" },
    ],
    whenThemFits:
      "Housecall Pro can fit when you have several techs and want heavy marketing automation and dispatch tooling, and you're comfortable moving up to the Essentials or MAX tier to unlock them.",
    whenJorvioFits:
      "Jorvio is a good fit when you want one flat price with no per-feature add-ons. Housecall Pro Basic starts at $79/mo ($59 annual) and locks GPS and core sales tools behind the $149+ Essentials tier. Tools like Sales Proposal and Price Book are paid add-ons. Jorvio includes quotes, portal, jobs, calendar, and payment tracking in the base price.",
  },
  {
    slug: "pandadoc",
    publish: true,
    metaTitle: "Jorvio Quotes vs PandaDoc for contractors",
    metaDescription:
      "PandaDoc is built for documents and e-sign. Jorvio is built for contractors who quote, schedule, and collect payment.",
    competitorName: "PandaDoc",
    competitorHint: "pandadoc.com",
    rows: [
      { feature: "Pricing model", jorvio: `from $${starterPrice}/mo`, them: "$19–$49 per user/mo" },
      { feature: "Built for field service contractors", jorvio: true, them: false },
      { feature: "Line-item estimates & trade templates", jorvio: true, them: "Generic templates" },
      { feature: "Customer portal with pay & sign", jorvio: true, them: "Document-centric" },
      { feature: "Jobs, calendar & crew scheduling", jorvio: true, them: false },
      { feature: "Deposits & balance payments (Stripe)", jorvio: true, them: "Payments add-on" },
      { feature: "AI quote drafting from job description", jorvio: true, them: false },
      { feature: "Live material price finder with buy links (Pro & Agency)", jorvio: true, them: false },
      { feature: "Strong template library & e-sign", jorvio: true, them: true },
    ],
    whenThemFits:
      "PandaDoc fits when you mainly need polished documents and e-signatures across many business types, and you already run scheduling and payments elsewhere.",
    whenJorvioFits:
      "Jorvio is a good fit when you want the whole loop in one place: quote, customer approval, deposit, job, calendar, and payment tracking. Built for trades, not generic document workflows.",
  },
  {
    slug: "proposify",
    publish: true,
    metaTitle: "Jorvio Quotes vs Proposify for contractors",
    metaDescription:
      "Proposify focuses on proposal documents. Jorvio connects quotes to jobs, scheduling, and contractor payments.",
    competitorName: "Proposify",
    competitorHint: "proposify.com",
    rows: [
      { feature: "Pricing & send limits", jorvio: `from $${starterPrice}/mo, unlimited sends`, them: "$29/user/mo, ~5 sends/mo (Basic)" },
      { feature: "Built for home-service contractors", jorvio: true, them: false },
      { feature: "Customer portal with pay & sign", jorvio: true, them: true },
      { feature: "Jobs linked to accepted quotes", jorvio: true, them: false },
      { feature: "Calendar & crew scheduling", jorvio: true, them: false },
      { feature: "Deposits & card checkout on quote", jorvio: true, them: "Limited / integrations" },
      { feature: "Proposal design & content library", jorvio: true, them: true },
      { feature: "AI quote drafting", jorvio: true, them: false },
      { feature: "Live material price finder with buy links (Pro & Agency)", jorvio: true, them: false },
    ],
    whenThemFits:
      "Proposify fits agencies and teams that live in proposal documents and already use separate tools for scheduling and field operations.",
    whenJorvioFits:
      "Jorvio is a good fit when sold work should become a job with payment tracking, not just a signed PDF. Run quotes, calendar, and payouts in one workspace.",
  },
  {
    slug: "honeybook",
    publish: true,
    metaTitle: "Jorvio Quotes vs HoneyBook for contractors",
    metaDescription:
      "HoneyBook is popular with creatives. Jorvio is built for contractors who schedule crews and track job payments.",
    competitorName: "HoneyBook",
    competitorHint: "honeybook.com",
    rows: [
      { feature: "Built for trades & field service", jorvio: true, them: "Creatives & events focus" },
      { feature: "Line-item trade estimates", jorvio: true, them: "Package-style proposals" },
      { feature: "Jobs, projects & calendar", jorvio: true, them: "Booking-focused" },
      { feature: "Crew or subcontractor scheduling", jorvio: true, them: false },
      { feature: "Customer portal with pay & sign", jorvio: true, them: true },
      { feature: "Payment tracking per job / quote", jorvio: true, them: "Invoice-led" },
      { feature: "Client experience & templates", jorvio: true, them: true },
    ],
    whenThemFits:
      "HoneyBook fits photographers, planners, and creative freelancers who want a polished client experience and don't need field-service scheduling.",
    whenJorvioFits:
      "Jorvio fits HVAC, plumbing, painting, and other trades who quote line-item work, schedule crews, group jobs under projects, and need to see paid vs. due on each job.",
  },
  {
    slug: "spreadsheets",
    publish: true,
    metaTitle: "Jorvio Quotes vs spreadsheets for contractors",
    metaDescription:
      "Stop rebuilding estimates in Excel. Send a professional link, get signature and deposit, and track the job in one place.",
    competitorName: "Spreadsheets & email",
    rows: [
      { feature: "Professional customer link (not an attachment)", jorvio: true, them: false },
      { feature: "Customer signature & acceptance", jorvio: true, them: "Manual / PDF export" },
      { feature: "Card deposit or payment on the quote", jorvio: true, them: false },
      { feature: "View tracking & follow-up automation", jorvio: true, them: false },
      { feature: "In-app customer messaging", jorvio: true, them: false },
      { feature: "Scheduled quote send", jorvio: true, them: false },
      { feature: "Jobs, calendar & payment rollup", jorvio: true, them: false },
      { feature: "Unlimited quotes with branding", jorvio: true, them: true },
      { feature: "Zero monthly software cost", jorvio: false, them: true },
    ],
    whenThemFits:
      "A spreadsheet still works for very occasional one-off quotes when you don't need signatures, deposits, or a shared record for office and field.",
    whenJorvioFits:
      "Jorvio is a good fit when slow quotes cost you jobs and customers expect a clean link instead of a PDF. Accepted work lands on your calendar with payment status, without duct-taping five tools together.",
  },
];

export const PUBLISHED_COMPARE_PAGES = COMPARE_PAGES.filter((p) => p.publish);

export const COMPARE_PAGE_BY_SLUG = Object.fromEntries(COMPARE_PAGES.map((p) => [p.slug, p])) as Record<
  string,
  ComparePage
>;

export function getComparePage(slug: string): ComparePage | undefined {
  const page = COMPARE_PAGE_BY_SLUG[slug];
  return page?.publish ? page : undefined;
}
